import React from 'react';
import ReferenceAhaComponent from './components/ReferenceAhaComponent';
import MenuItemComponent from './components/MenuItemComponent';
import {
  AhaEditorUtils,
  type AhaReferenceElement,
  type ComponentProps,
  type EditorPlugin,
  MenuPluginFactory,
  type ReferenceAhaObject,
  slate,
} from '@aha-app/aha-editor-v2';
import * as AbortableRequest from '../../AbortableRequest';
import { getVisibleFieldsForItemClass } from './utils';
import type BatchedRecordLoader from 'components/framework/BatchedRecordLoader';
import PasteReferenceLinkPlugin from './PasteReferenceLinkPlugin';

// Aha! Record Reference Plugin
// This plugin allows users to add Aha! record references to their content. It's a
// function that returns two plugins:
// 1. A component plugin for rendering the reference element in the editor
// 2. A menu plugin that provides search and selection functionality for adding new references. This
//    plugin uses the MenuPluginFactory and is triggered by the '#' character.

interface ReferenceAhaPluginOptions {
  // Callback function to be called when a reference is inserted
  onInsertReference?: (reference: ReferenceAhaObject) => void;
  // Whether the fields displayed in the reference component are modifiable
  isModifiable: () => boolean;
  // Instance of BatchedRecordLoader, used to load multiple records in a single GQL request
  batchedRecordLoader: BatchedRecordLoader;
}

// Plugin to allow # mentioning Aha! records in the editor.
const ReferenceAhaPlugin = ({
  onInsertReference,
  isModifiable,
  batchedRecordLoader,
}: ReferenceAhaPluginOptions): EditorPlugin[] => {
  const requestCache = AbortableRequest.createRequestCache();

  // Menu plugin for searching and selecting Aha! references
  const ReferenceAhaMenuPlugin = MenuPluginFactory<ReferenceAhaObject>({
    name: 'aha-reference',
    triggerCharacter: '#',
    menuItemUniqueKey: 'id',
    minCharacters: 0,
    searchingPlaceholder: 'Searching records...',
    onSearch: ({ text }) => {
      function pick(text: string) {
        const url = `/search/references.json?q=${text}*`;
        return AbortableRequest.create(
          url,
          (response: { items: ReferenceAhaObject[] }): ReferenceAhaObject[] => {
            const { items } = response;

            return items.filter(Boolean).map(item => ({
              ...item,
              visibleFields: getVisibleFieldsForItemClass(item.class),
            }));
          }
        );
      }

      requestCache.abort();
      text = text.toLowerCase().trim();
      return requestCache.add(text, () => pick(text));
    },
    MenuItemComponent,
    onSelect: ({ item: reference, editor }) => {
      const visibleFields = getVisibleFieldsForItemClass(reference.class);

      AhaEditorUtils.Marks.withRestoredMarks(editor, editor => {
        // Insert the record reference at the current selection
        slate.Transforms.insertNodes(editor, {
          type: 'aha-reference',
          reference_class: reference.class,
          reference_id: reference.id,
          reference_name: reference.name,
          reference_url: reference.url,
          reference_num: reference.reference_num,
          reference_visibleFields: visibleFields,
          children: [{ text: '' }],
        });

        // Move cursor after the inserted node
        AhaEditorUtils.Selection.moveAfterCurrentNode(editor);
      });

      slate.Transforms.insertText(editor, ' ');

      if (onInsertReference) {
        onInsertReference(reference);
      }
    },
  });

  const ReferenceAha: React.FC<ComponentProps<AhaReferenceElement>> = props => {
    return (
      <ReferenceAhaComponent
        {...props}
        isModifiable={isModifiable()}
        batchedRecordLoader={batchedRecordLoader}
      />
    );
  };

  // Component plugin for rendering the Aha! reference element
  const ReferenceAhaComponentPlugin: EditorPlugin = {
    name: 'Reference aha component plugin',
    components: [
      {
        type: 'aha-reference',
        component: ReferenceAha,
      },
    ],
  };

  return [
    ReferenceAhaComponentPlugin,
    ReferenceAhaMenuPlugin,
    PasteReferenceLinkPlugin({ onInsertReference }),
  ];
};

export default ReferenceAhaPlugin;
